import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Icons } from "../../components/admin/Icons";
import { useConfirm } from "../../hooks/useConfirm";

/* ════════════════════════════════════════
   CONFIG RÔLES
════════════════════════════════════════ */
const ROLE_CFG = {
  directeur: { label: "Directeur", bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
  formateur: { label: "Formateur", bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
  pole:      { label: "Pôle",      bg: "#faf5ff", color: "#7c3aed", border: "#e9d5ff" },
};

const AV_COLORS = {
  directeur: { bg: "var(--g0)",  color: "var(--g6)"  },
  formateur: { bg: "var(--p0)",  color: "var(--p6)"  },
  pole:      { bg: "#faf5ff",    color: "#7c3aed"     },
};

/* ── Helpers ── */
function RoleBadge({ role }) {
  const c = ROLE_CFG[role] || { label: role, bg: "var(--n1)", color: "var(--n6)", border: "var(--border)" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 20,
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      fontSize: 11, fontWeight: 700, letterSpacing: .3,
    }}>
      {c.label}
    </span>
  );
}

function getInitials(name = "") {
  return name.split(" ").filter(Boolean).map(w => w[0]).join("").toUpperCase().slice(0, 2);
}

function generateEmail(nom) {
  if (!nom) return "";
  return nom
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .trim()
    .split(/\s+/)
    .join(".") + "@gmail.com";
}

function generatePoleMail(nom) {
  if (!nom) return "";
  return nom
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .trim()
    .split(/\s+/)
    .join(".") + ".pole@gmail.com";
}

/* ════════════════════════════════════════
   COMPOSANT PRINCIPAL
════════════════════════════════════════ */
export default function Utilisateurs() {
  const [data, setData]       = useState([]);
  const [meta, setMeta]       = useState(null);
  const [counts, setCounts]   = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [filterRole, setRole] = useState("");
  const [page, setPage]       = useState(1);
  const [alert, setAlert]     = useState(null);
  

  /* Modal */
  const [modal, setModal]     = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [errors, setErrors]   = useState({});
  const [form, setForm] = useState({
    name: "", email: "",
    password: "", password_confirmation: "",
    role: "formateur",
    formateur_id: "",
    secteur_id: "",
  });

  const [options, setOptions] = useState({
    formateurs_disponibles: [],
    formateurs_tous:        [],
    secteurs:               [],
  });

  const [confirm, ConfirmDialog] = useConfirm();

  /* ── Flash ── */
  const flash = (msg, type = "ok") => {
    setAlert({ msg, type });
    setTimeout(() => setAlert(null), 3500);
  };

  const setField = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  /* ── Fetch liste principale ── */
  const fetchData = useCallback(() => {
    setLoading(true);
    axios.get("/users", { params: { search, role: filterRole, page } })
      .then(r => {
        setData(r.data.data || []);
        setMeta(r.data);
        setCounts(r.data.counts || {});
      })
      .catch(() => flash("Erreur de chargement.", "err"))
      .finally(() => setLoading(false));
  }, [search, filterRole, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── Fetch options dropdowns ── */
  const fetchOptions = (userId = null) => {
    const params = userId ? { editing_user_id: userId } : {};
    return axios.get("/users/options", { params })
      .then(r => {
        setOptions({
          formateurs_disponibles: r.data.formateurs_disponibles || [],
          formateurs_tous:        r.data.formateurs_tous        || [],
          secteurs:               r.data.secteurs               || [],
        });
      })
      .catch(() => {});
  };

  /* ════════════════════════════════════════
     HANDLERS SELECT
  ════════════════════════════════════════ */

  const handleFormateurChange = (e) => {
    const id = e.target.value;
    const f  = options.formateurs_disponibles.find(x => String(x.id) === String(id));
    setForm(p => ({
      ...p,
      formateur_id: id,
      name:  f ? f.nom : p.name,
      email: f ? generateEmail(f.nom) : p.email,
    }));
  };

  const handlePoleFormateurChange = (e) => {
    const id = e.target.value;
    const f  = options.formateurs_tous.find(x => String(x.id) === String(id));
    setForm(p => ({
      ...p,
      formateur_id: id,
      name:  f ? f.nom : p.name,
      email: f ? generateEmail(f.nom) : p.email,
    }));
  };

  const handleSecteurChange = (e) => {
    const sid = e.target.value;
    const secteur = options.secteurs.find(s => String(s.id) === String(sid));
    const resp = secteur?.responsable;
    if (resp) {
      setForm(p => ({
        ...p,
        secteur_id:   sid,
        formateur_id: String(resp.id),
        name:         resp.nom ?? "",
        email:        generateEmail(resp.nom ?? ""),
      }));
    } else {
      setForm(p => ({ ...p, secteur_id: sid, formateur_id: "", name: "", email: "" }));
    }
  };

  /* ── Changement rôle : reset formateur/secteur, conserver name/email si même rôle ── */
  const handleRoleChange = (newRole) => {
    setForm(p => ({
      ...p,
      role:         newRole,
      formateur_id: "",
      secteur_id:   "",
      name:         newRole !== p.role ? "" : p.name,
      email:        newRole !== p.role ? "" : p.email,
    }));
  };

  /* ── Modal open/close ── */
  const openAdd = async () => {
    setEditing(null);
    setForm({
      name: "", email: "",
      password: "", password_confirmation: "",
      role: "formateur",
      formateur_id: "", secteur_id: "",
    });
    setErrors({});
    await fetchOptions();
    setModal(true);
  };

  const openEdit = async (u) => {
    setEditing(u);
    setForm({
      name:                  u.name,
      email:                 u.email,
      password:              "",
      password_confirmation: "",
      role:                  u.role,
      formateur_id:          u.formateur_id ? String(u.formateur_id) : "",
      secteur_id:            u.secteur_id   ? String(u.secteur_id)   : "",
    });
    setErrors({});
    await fetchOptions(u.id);
    setModal(true);
  };

  /* ── Submit ── */
  const submit = async () => {
    setSaving(true);
    setErrors({});
    try {
      if (editing) {
        await axios.put(`/users/${editing.id}`, form);
        flash("Utilisateur mis à jour avec succès.");
      } else {
        await axios.post("/users", form);
        flash("Utilisateur créé avec succès.");
      }
      setModal(false);
      if (!editing) { setSearch(""); setRole(""); }
      setPage(1);
      axios.get("/users", { params: { page: 1 } })
        .then(r => { setData(r.data.data || []); setMeta(r.data); setCounts(r.data.counts || {}); })
        .catch(() => {});
    } catch (e) {
      if (e.response?.status === 422) setErrors(e.response.data.errors || {});
      else flash(e.response?.data?.message || "Impossible d'enregistrer l'utilisateur. Vérifiez les informations saisies.", "err");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const remove = async (u) => {
    const ok = await confirm({
      title: `Supprimer le compte de ${u.name} ?`,
      message: "Ce compte sera définitivement supprimé. L'utilisateur ne pourra plus se connecter.",
      confirmLabel: "Supprimer le compte",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await axios.delete(`/users/${u.id}`);
      flash("Compte supprimé avec succès.");
      fetchData();
    } catch (e) {
      flash(e.response?.data?.message || "La suppression a échoué. Veuillez réessayer.", "err");
    }
  };

  /* ── Filtres ── */
  const hasFilters   = !!(search || filterRole);
  const activeCount  = [search, filterRole].filter(Boolean).length;
  const resetFilters = () => { setSearch(""); setRole(""); setPage(1); };

  /* ── Colonne "Secteur" ── */
  const renderSecteur = (u) => {
    if (u.role === "pole") {
      return u.secteur ? (
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ color: "#7c3aed", display: "flex" }}>{Icons.target}</span>
          <strong style={{ fontSize: 12 }}>{u.secteur.nom}</strong>
        </span>
      ) : <em style={{ color: "var(--sl3)", fontSize: 12 }}>—</em>;
    }
    return <em style={{ color: "var(--sl3)", fontSize: 12 }}>—</em>;
  };

  /* ════════════════════════════════════════
     RENDU PRINCIPAL
  ════════════════════════════════════════ */
  return (
    <div>

      {/* ── Header ── */}
      <div className="pg-header">
        <div className="pg-header-left">
          <div className="pg-title">Utilisateurs</div>
          <div className="pg-subtitle">
            {meta?.total ?? "—"} compte(s) enregistré(s) — gestion des accès par rôle
          </div>
        </div>
        <div className="pg-actions">
          <button className="btn-primary" onClick={openAdd}>
            {Icons.plus} Ajouter un utilisateur
          </button>
        </div>
      </div>

      {/* ── Alert flash ── */}
      {alert && (
        <div className={`al-alert al-alert-${alert.type}`}>
          {alert.type === "ok" ? Icons.check : Icons.alert}
          {alert.msg}
        </div>
      )}

      {/* ── KPI cards ── */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        {Object.entries(ROLE_CFG).map(([role, cfg]) => (
          <div key={role}
            onClick={() => { setRole(filterRole === role ? "" : role); setPage(1); }}
            style={{
              flex: "1 1 150px", display: "flex", alignItems: "center", gap: 12,
              padding: "14px 18px", borderRadius: 12, cursor: "pointer",
              background: cfg.bg,
              border: `1.5px solid ${filterRole === role ? cfg.color : cfg.border}`,
              outline: filterRole === role ? `2px solid ${cfg.color}` : "2px solid transparent",
              transform: filterRole === role ? "translateY(-2px)" : "none",
              boxShadow: filterRole === role ? `0 4px 14px ${cfg.color}22` : "none",
              transition: "all .15s", userSelect: "none",
            }}
          >
            <div>
              <div style={{
                fontSize: 26, fontWeight: 800, color: cfg.color, lineHeight: 1,
                fontFamily: "var(--font-hd)", fontVariantNumeric: "tabular-nums",
              }}>
                {counts[role] ?? "—"}
              </div>
              <div style={{ fontSize: 12, color: "var(--sl5)", marginTop: 2, fontWeight: 500 }}>
                {cfg.label}s
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Table card ── */}
      <div className="table-card">

        {/* Filtres */}
        <div className="filter-panel-inline">
          <div className="filter-panel-header">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--sl7)" }}>Recherche & Filtres</span>
              {activeCount > 0 && (
                <span style={{
                  background: "var(--g4)", color: "#111",
                  fontSize: 10, fontWeight: 700,
                  padding: "2px 7px", borderRadius: 20,
                }}>
                  {activeCount}
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {hasFilters && (
                <button className="btn-reset" onClick={resetFilters}>
                  {Icons.close} Réinitialiser
                </button>
              )}
              <span className="results-count">{meta?.total ?? 0} résultat(s)</span>
            </div>
          </div>

          <div className="filter-row">
            <div style={{ position: "relative", flex: "1 1 220px", minWidth: 170 }}>
              <span className="search-icon">{Icons.search}</span>
              <input
                className="search-input filter-input"
                placeholder="Nom ou email..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            <select
              className="form-select filter-select"
              style={{ height: 36, width: 170 }}
              value={filterRole}
              onChange={e => { setRole(e.target.value); setPage(1); }}
            >
              <option value="">Tous les rôles</option>
              <option value="directeur">Directeur</option>
              <option value="formateur">Formateur</option>
              <option value="pole">Pôle</option>
            </select>
          </div>
        </div>

        {/* Tableau */}
        {loading ? (
          <div className="loader">
            <div className="loader-spinner" />
            <span>Chargement...</span>
          </div>
        ) : data.length === 0 ? (
          <div className="empty">
            <div className="empty-title">Aucun utilisateur trouvé</div>
            <div className="empty-desc">Créez des comptes pour vos formateurs et responsables de pôle</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table style={{ minWidth: 800 }}>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Utilisateur</th>
                  <th>Email</th>
                  <th>Rôle</th>
                  <th>Secteur</th>
                  <th>Mot de passe</th>
                  <th>Statut</th>
                  <th style={{ width: 90 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((u, i) => {
                  const av = AV_COLORS[u.role] || { bg: "var(--n1)", color: "var(--n5)" };
                  const displayPassword = u.plain_password || "—";
                  return (
                    <tr key={u.id}>
                      <td style={{ color: "var(--sl4)", fontVariantNumeric: "tabular-nums" }}>
                        {(page - 1) * 15 + i + 1}
                      </td>

                      {/* Utilisateur */}
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{
                            width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                            background: av.bg, color: av.color,
                            border: `1.5px solid ${av.color}30`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 12, fontWeight: 700, fontFamily: "var(--font-hd)",
                          }}>
                            {getInitials(u.name)}
                          </div>
                          <strong style={{ color: "var(--sl8)", fontSize: 13.5 }}>{u.name}</strong>
                        </div>
                      </td>

                      {/* Email */}
                      <td>
                        <span style={{ fontSize: 12.5, color: "var(--sl5)", fontFamily: "var(--font-mono)" }}>
                          {u.email}
                        </span>
                      </td>

                      {/* Rôle */}
                      <td><RoleBadge role={u.role} /></td>

                      {/* Secteur */}
                      <td style={{ fontSize: 12.5, color: "var(--sl6)" }}>
                        {renderSecteur(u)}
                      </td>

                      {/* Mot de passe - Affiché en clair */}
                      <td>
                        <span style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 13,
                          color: u.plain_password ? "#059669" : "#9ca3af",
                          fontWeight: 600,
                          backgroundColor: u.plain_password ? "#ecfdf5" : "#f3f4f6",
                          padding: "4px 8px",
                          borderRadius: 6,
                          display: "inline-block",
                        }}>
                          {displayPassword}
                        </span>
                      </td>

                      {/* Statut */}
                      <td>
                        <span className={`badge ${u.statut === "actif" ? "badge-ok" : "badge-off"}`}>
                          {u.statut === "actif" ? "Actif" : "Inactif"}
                        </span>
                      </td>

                      {/* Actions */}
                      <td>
                        <button className="btn-icon btn-icon-edit" title="Modifier" onClick={() => openEdit(u)}>
                          {Icons.edit}
                        </button>
                        {u.role !== "directeur" && (
                          <button className="btn-icon btn-icon-del" title="Supprimer" onClick={() => remove(u)}>
                            {Icons.trash}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {meta?.last_page > 1 && (
          <div className="pagination">
            <button className="pg-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            {Array.from({ length: meta.last_page }, (_, i) => i + 1)
              .filter(p => p === 1 || p === meta.last_page || Math.abs(p - page) <= 2)
              .map((p, idx, arr) => (
                <span key={p}>
                  {idx > 0 && arr[idx - 1] !== p - 1 && (
                    <span style={{ padding: "0 4px", color: "var(--sl4)" }}>…</span>
                  )}
                  <button className={`pg-btn ${page === p ? "active" : ""}`} onClick={() => setPage(p)}>
                    {p}
                  </button>
                </span>
              ))}
            <button className="pg-btn" onClick={() => setPage(p => Math.min(meta.last_page, p + 1))} disabled={page === meta.last_page}>›</button>
          </div>
        )}
      </div>

      {ConfirmDialog}

      {/* ════════════════════════════════════════
          MODAL
      ════════════════════════════════════════ */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal" style={{ width: 560 }}>

            <div className="modal-header">
              <div className="modal-title">
                {editing ? "Modifier l'utilisateur" : "Nouvel utilisateur"}
              </div>
              <button className="modal-close" onClick={() => setModal(false)}>{Icons.close}</button>
            </div>

            {/* ── Sélection du rôle ── */}
            <div className="form-group">
              <label className="form-label">Rôle *</label>
              <div style={{ display: "flex", gap: 8 }}>
                {Object.entries(ROLE_CFG).map(([role, cfg]) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => handleRoleChange(role)}
                    style={{
                      flex: 1, padding: "10px 0", borderRadius: 10, cursor: "pointer",
                      border: `2px solid ${form.role === role ? cfg.color : "var(--border)"}`,
                      background: form.role === role ? cfg.bg : "var(--n0)",
                      color: form.role === role ? cfg.color : "var(--n5)",
                      fontWeight: 600, fontSize: 12, transition: "all .15s",
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 5,
                      boxShadow: form.role === role ? `0 2px 10px ${cfg.color}22` : "none",
                    }}
                  >
                    {cfg.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ════ RÔLE : FORMATEUR ════ */}
            {form.role === "formateur" && (
              <>
                <div className="form-group">
                  <label className="form-label">Nom du formateur *</label>
                  {options.formateurs_disponibles.length === 0 ? (
                    <div style={{
                      padding: "10px 14px", borderRadius: 8,
                      background: "#fffbeb", border: "1px solid #fde68a",
                      fontSize: 12, color: "#92400e",
                    }}>
                      Tous les formateurs actifs ont déjà un compte utilisateur.
                    </div>
                  ) : (
                    <select className="form-select" value={form.formateur_id} onChange={handleFormateurChange}>
                      <option value="">— Sélectionner un formateur —</option>
                      {options.formateurs_disponibles.map(f => (
                        <option key={f.id} value={f.id}>{f.nom}</option>
                      ))}
                    </select>
                  )}
                  {errors.formateur_id && <div className="field-err">{errors.formateur_id[0]}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Email *
                    {form.formateur_id && (
                      <span style={{ marginLeft: 6, fontSize: 10, color: "var(--sl4)", fontWeight: 400, textTransform: "none" }}>
                        (généré automatiquement — modifiable)
                      </span>
                    )}
                  </label>
                  <input className="form-input" type="email" placeholder="email@gmail.com"
                    value={form.email} onChange={setField("email")} />
                  {errors.email && <div className="field-err">{errors.email[0]}</div>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      Mot de passe {editing
                        ? <span style={{ color: "var(--sl4)", fontWeight: 400, textTransform: "none" }}>(vide = inchangé)</span>
                        : "*"}
                    </label>
                    <input className="form-input" type="password" placeholder="••••••••"
                      value={form.password} onChange={setField("password")} />
                    {errors.password && <div className="field-err">{errors.password[0]}</div>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Confirmer</label>
                    <input className="form-input" type="password" placeholder="••••••••"
                      value={form.password_confirmation} onChange={setField("password_confirmation")} />
                  </div>
                </div>
              </>
            )}

            {/* ════ RÔLE : PÔLE ════ */}
            {form.role === "pole" && (
              <>
                {/* 1. Secteur first — auto-fills responsable */}
                <div className="form-group">
                  <label className="form-label">Secteur / Pôle *</label>
                  {options.secteurs.length === 0 ? (
                    <div style={{
                      padding: "10px 14px", borderRadius: 8,
                      background: "#fffbeb", border: "1px solid #fde68a",
                      fontSize: 12, color: "#92400e",
                    }}>
                      Tous les secteurs ont déjà un responsable de pôle.
                    </div>
                  ) : (
                    <select className="form-select" value={form.secteur_id} onChange={handleSecteurChange}>
                      <option value="">— Sélectionner un secteur —</option>
                      {options.secteurs.map(s => (
                        <option key={s.id} value={s.id}>{s.nom}</option>
                      ))}
                    </select>
                  )}
                  {errors.secteur_id && <div className="field-err">{errors.secteur_id[0]}</div>}
                  {form.secteur_id && !options.secteurs.find(s => String(s.id) === String(form.secteur_id))?.responsable && (
                    <div style={{ fontSize: 11, color: "var(--sl4)", marginTop: 4 }}>
                      Aucun responsable assigné pour ce secteur.
                    </div>
                  )}
                </div>

                {/* 2. Responsable — auto-filled from secteur, manually overridable */}
                <div className="form-group">
                  <label className="form-label">
                    Responsable (formateur) *
                    {form.secteur_id && form.formateur_id && (
                      <span style={{ marginLeft: 6, fontSize: 10, color: "var(--sl4)", fontWeight: 400, textTransform: "none" }}>
                        (auto-rempli depuis l'assignation du pôle — modifiable)
                      </span>
                    )}
                  </label>
                  {options.formateurs_tous.length === 0 ? (
                    <div style={{
                      padding: "10px 14px", borderRadius: 8,
                      background: "#fffbeb", border: "1px solid #fde68a",
                      fontSize: 12, color: "#92400e",
                    }}>
                      Aucun formateur actif disponible.
                    </div>
                  ) : (
                    <select className="form-select" value={form.formateur_id} onChange={handlePoleFormateurChange}>
                      <option value="">— Sélectionner le responsable —</option>
                      {options.formateurs_tous.map(f => (
                        <option key={f.id} value={f.id}>{f.nom}</option>
                      ))}
                    </select>
                  )}
                  {errors.formateur_id && <div className="field-err">{errors.formateur_id[0]}</div>}
                </div>

                {/* 3. Email conflict warning */}
                {form.formateur_id &&
                  !options.formateurs_disponibles.find(f => String(f.id) === String(form.formateur_id)) && (
                  <div style={{
                    padding: "10px 14px", borderRadius: 8, marginBottom: 4,
                    background: "#fffbeb", border: "1px solid #fde68a",
                    fontSize: 12, color: "#92400e",
                    display: "flex", flexDirection: "column", gap: 6,
                  }}>
                    <div style={{ fontWeight: 600 }}>
                      Ce formateur possède déjà un compte formateur actif.
                    </div>
                    <div>
                      Son email est peut-être déjà utilisé. Pour éviter un conflit, utilisez une adresse dédiée au rôle pôle.
                    </div>
                    {form.name && (
                      <button
                        type="button"
                        onClick={() => setForm(p => ({ ...p, email: generatePoleMail(p.name) }))}
                        style={{
                          alignSelf: "flex-start", padding: "4px 10px", borderRadius: 6, cursor: "pointer",
                          border: "1px solid #d97706", background: "#fef3c7", color: "#92400e",
                          fontSize: 11, fontWeight: 600,
                        }}
                      >
                        Utiliser : {generatePoleMail(form.name)}
                      </button>
                    )}
                  </div>
                )}

                {/* 4. Email */}
                <div className="form-group">
                  <label className="form-label">
                    Email *
                    {form.formateur_id && (
                      <span style={{ marginLeft: 6, fontSize: 10, color: "var(--sl4)", fontWeight: 400, textTransform: "none" }}>
                        (généré automatiquement — modifiable)
                      </span>
                    )}
                  </label>
                  <input className="form-input" type="email" placeholder="email@gmail.com"
                    value={form.email} onChange={setField("email")} />
                  {errors.email && <div className="field-err">{errors.email[0]}</div>}
                </div>

                {/* 5. Password */}
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      Mot de passe {editing
                        ? <span style={{ color: "var(--sl4)", fontWeight: 400, textTransform: "none" }}>(vide = inchangé)</span>
                        : "*"}
                    </label>
                    <input className="form-input" type="password" placeholder="••••••••"
                      value={form.password} onChange={setField("password")} />
                    {errors.password && <div className="field-err">{errors.password[0]}</div>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Confirmer</label>
                    <input className="form-input" type="password" placeholder="••••••••"
                      value={form.password_confirmation} onChange={setField("password_confirmation")} />
                  </div>
                </div>
              </>
            )}

            {/* ════ RÔLE : DIRECTEUR ════ */}
            {form.role === "directeur" && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Nom complet *</label>
                    <input className="form-input" placeholder="Ex: AMINE MAJID"
                      value={form.name} onChange={setField("name")} />
                    {errors.name && <div className="field-err">{errors.name[0]}</div>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input className="form-input" type="email" placeholder="email@gmail.com"
                      value={form.email} onChange={setField("email")} />
                    {errors.email && <div className="field-err">{errors.email[0]}</div>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      Mot de passe {editing
                        ? <span style={{ color: "var(--sl4)", fontWeight: 400, textTransform: "none" }}>(vide = inchangé)</span>
                        : "*"}
                    </label>
                    <input className="form-input" type="password" placeholder="••••••••"
                      value={form.password} onChange={setField("password")} />
                    {errors.password && <div className="field-err">{errors.password[0]}</div>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Confirmer</label>
                    <input className="form-input" type="password" placeholder="••••••••"
                      value={form.password_confirmation} onChange={setField("password_confirmation")} />
                  </div>
                </div>

                <div style={{
                  padding: "11px 14px", borderRadius: 9, marginBottom: 14,
                  background: "var(--g0)", border: "1px solid var(--g1)",
                  fontSize: 12.5, color: "var(--g6)",
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  Ce compte aura accès complet au tableau de bord directeur.
                </div>
              </>
            )}

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setModal(false)}>Annuler</button>
              <button className="btn-primary" onClick={submit} disabled={saving}>
                {saving ? "Enregistrement…" : editing ? "Mettre à jour" : "Créer l'utilisateur"}
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}